// src/pages/goodsReceivedNote/GoodsReceivedNoteListPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PackageCheck, Plus, Eye, Calendar, Clock, Undo2, FileText, Warehouse, XCircle } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import { getGoodsReceivedNotes } from '../../services/api/procurement/grn';
import { getUserWarehouses } from '../../services/api/purchaseOrder';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { PURCHASE_ORDER_BASE_PATH, buildProcurementCreatePath, buildReturnState } from '../../utils/procurementNavigation';
import DocNumberCell from '../procurement/components/DocNumberCell';
import tb from '../procurement/components/ProcurementTable.module.css';
import styles from './GoodsReceivedNoteListPage.module.css';

// Server-side filters: warehouseId/status/dateFrom/dateTo, plus `search` (GRN or PO number,
// rettzy-backend .../procurement/services/grn.service.js) which backs the Purchase Order filter.
const STATUS_ICONS = { received: PackageCheck, cancelled: XCircle };

const statusConfig = {
  received: { color: '#10b981', bg: '#d1fae5' },
  cancelled: { color: '#ef4444', bg: '#fee2e2' },
};

const GoodsReceivedNoteListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  const [goodsReceivedNotes, setGoodsReceivedNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [warehouses, setWarehouses] = useState([]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100],
  });

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ warehouseId: '', poNumber: '', dateFrom: '', dateTo: '' });

  useEffect(() => {
    (async () => {
      try {
        const result = await getUserWarehouses();
        setWarehouses(result.warehouses || []);
      } catch (err) {
        console.error('Failed to load warehouses:', err);
      }
    })();
  }, []);

  useEffect(() => {
    fetchGoodsReceivedNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, filters]);

  const fetchGoodsReceivedNotes = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
      };
      if (filters.warehouseId) params.warehouseId = filters.warehouseId;
      if (filters.poNumber) params.search = filters.poNumber.trim();
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const response = await getGoodsReceivedNotes(params);
      setGoodsReceivedNotes(response.goodsReceivedNotes || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (err) {
      console.error('Failed to fetch goods received notes:', err);
      if (err.response?.status === 403) {
        showError(t('errors.permissionDenied'));
      } else {
        showError(t('goodsReceivedNote.failedToLoadGoodsReceivedNotes'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ warehouseId: '', poNumber: '', dateFrom: '', dateTo: '' });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  const hasActiveFilters = () => Object.values(filters).some(v => v !== '');

  const filterDefinitions = [
    {
      key: 'warehouseId',
      label: t('goodsReceivedNote.warehouseLabel'),
      type: 'select',
      placeholder: t('goodsReceivedNote.allWarehouses'),
      options: warehouses.map(w => ({ value: w.id.toString(), label: w.name })),
    },
    {
      key: 'poNumber',
      label: t('goodsReceivedNote.purchaseOrderNumber'),
      type: 'text',
      placeholder: t('goodsReceivedNote.purchaseOrderIdPlaceholder'),
    },
    {
      key: 'date',
      label: t('goodsReceivedNote.receivedAt'),
      type: 'daterange',
    },
  ];

  const columns = [
    {
      key: 'grnNumber',
      header: t('goodsReceivedNote.grnNumber'),
      width: '170px',
      render: (row) => <DocNumberCell number={row.grnNumber} to={`/procurement/grn/${row.id}`} />,
    },
    {
      key: 'purchaseOrder',
      header: t('goodsReceivedNote.purchaseOrderNumber'),
      width: '160px',
      render: (row) => row.purchaseOrder?.purchaseOrderNumber ? (
        <Link className={tb.docLink} to={`${PURCHASE_ORDER_BASE_PATH}/${row.purchaseOrder.id}`} onClick={(e) => e.stopPropagation()}>
          <FileText size={12} />{row.purchaseOrder.purchaseOrderNumber}
        </Link>
      ) : <span className={tb.naText}>—</span>,
    },
    {
      key: 'warehouse',
      header: t('goodsReceivedNote.warehouse'),
      width: '160px',
      render: (row) => <div className={tb.shopBadge}><Warehouse size={12} />{row.Warehouse?.name || t('common.na')}</div>,
    },
    {
      key: 'status',
      header: t('common.status'),
      width: '130px',
      render: (row) => {
        const cfg = statusConfig[row.status] || statusConfig.received;
        const StatusIcon = STATUS_ICONS[row.status] || Clock;
        return (
          <div className={tb.statusBadge} style={{ backgroundColor: cfg.bg, borderColor: cfg.color, color: cfg.color }}>
            <StatusIcon size={14} />
            <span>{t(`goodsReceivedNote.status_${row.status}`)}</span>
          </div>
        );
      },
    },
    {
      key: 'receivedAt',
      header: t('goodsReceivedNote.receivedAt'),
      width: '160px',
      render: (row) => (
        <div className={tb.dateInfo}>
          {row.receivedAt ? (<><Calendar size={12} />{formatDate(row.receivedAt)}</>) : t('common.na')}
          {row.receivedAt && <div className={tb.timeInfo}>{formatTime(row.receivedAt)}</div>}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: t('common.created'),
      width: '160px',
      render: (row) => (
        <div className={tb.dateCell}>
          <div className={tb.dateInfo}><Calendar size={12} />{formatDate(row.createdAt)}</div>
          <div className={tb.timeInfo}>{formatTime(row.createdAt)}</div>
        </div>
      ),
    },
  ];

  const tableActions = {
    render: (row) => (
      <div className={tb.actionsWrapper}>
        <Link
          to={`/procurement/grn/${row.id}`}
          className={`${tb.actionButton} ${tb.viewButton}`}
          title={t('common.viewDetails')}
        >
          <Eye size={14} />
        </Link>
        {row.status !== 'cancelled' && hasPermission('create_rtv') && (
          <Link
            to={buildProcurementCreatePath('/procurement/return-to-vendor/create', { goodsReceivedNoteId: row.id }, '/procurement/grn', t('goodsReceivedNote.allGoodsReceivedNotes'))}
            state={buildReturnState('/procurement/grn', t('goodsReceivedNote.allGoodsReceivedNotes'))}
            className={`${tb.actionButton} ${tb.actionWarning}`}
            title={t('returnToVendor.createReturnToVendor')}
            onClick={(e) => e.stopPropagation()}
          >
            <Undo2 size={14} />
          </Link>
        )}
      </div>
    ),
    width: '90px',
    frozen: true,
  };

  return (
    <div className={styles.goodsReceivedNoteListPage}>
      <PageHeader
        title={t('goodsReceivedNote.allGoodsReceivedNotes')}
        description={t('goodsReceivedNote.viewAndManageAllGoodsReceivedNotes')}
        icon={PackageCheck}
        actions={
          hasPermission('create_grn') && (
            <Link to="/procurement/grn/create" className={styles.primaryButton}>
              <Plus size={20} />
              {t('goodsReceivedNote.createGoodsReceivedNote')}
            </Link>
          )
        }
      />

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      <DataTable
        data={goodsReceivedNotes}
        columns={columns}
        actions={tableActions}
        onRowClick={(row) => navigate(`/procurement/grn/${row.id}`)}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          hasActiveFilters() ? t('goodsReceivedNote.noGoodsReceivedNotesFound') : t('goodsReceivedNote.noGoodsReceivedNotesYet')
        }
        emptyDescription={
          hasActiveFilters()
            ? t('goodsReceivedNote.noGoodsReceivedNotesFoundDescription')
            : t('goodsReceivedNote.noGoodsReceivedNotesYetDescription')
        }
        emptyIcon={<PackageCheck size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={handleClearFilters} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('create_grn') ? (
            <Link to="/procurement/grn/create" className={styles.primaryButton}>
              <Plus size={18} />
              {t('goodsReceivedNote.createFirstGoodsReceivedNote')}
            </Link>
          ) : null
        }
      />
    </div>
  );
};

export default GoodsReceivedNoteListPage;
